// ============================================================================
// checklistReader — read API for the book model, projected into the legacy
// ChecklistItem shape.
// ----------------------------------------------------------------------------
// Pair to `checklistWriter`. Every read site (MemberTodos, FormsLayout,
// ForYouPage, AuditHub, InvitesHubV2, FieldPassesPanel, useMeetingReadiness,
// helperAssignedItemsService, deterministicReportBuilders,
// reportGenerationService) now imports from here.
//
// Reads flow through the new model:
//   1. Query `checklists` collection (definitions) with optional filters.
//   2. Load today's page for each definition's book (batched by book).
//   3. Load the layout version referenced by each page.
//   4. Project each (definition, completion?, layoutNode?) into a
//      FlatChecklistItem via projectDefinitionToChecklistItem.
//
// Because the shape matches the legacy ChecklistItem exactly, downstream
// consumers don't need to change.
// ============================================================================

import {
  collection,
  doc,
  getDocFromServer,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { scopedReadConstraints } from './accessScopeService'
import type { ChecklistItem } from '../types/schema'
import {
  bookIdFor,
  CHECKLISTS_COL,
  type ChecklistBookPage,
  type ChecklistDefinition,
  type ChecklistDetailVersion,
  type ChecklistLayoutVersion,
  type ChecklistType,
} from '../types/checklistBooks'
import { getPage, pageKeyFor } from './checklistBookService'
import { getLayoutVersion } from './layoutVersionService'
import { getDetailVersion } from './checklistDetailVersionService'
import { projectDefinitionToChecklistItem } from './checklistViewAdapter'
import { primeChecklistPageCache } from './checklistReadCache'
import { fetchWorkspaceRawTimezone } from './checklistStatusWriter'

export type ChecklistFilter = {
  workspaceId: string
  parentContainerId?: string
  assigneeId?: string
  checklistType?: ChecklistType
  includeArchived?: boolean
  timeZone?: string
}

const buildConstraints = (filter: ChecklistFilter): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [where('workspaceId', '==', filter.workspaceId)]
  if (filter.parentContainerId) constraints.push(where('parentContainerId', '==', filter.parentContainerId))
  if (filter.assigneeId) constraints.push(where('assigneeId', '==', filter.assigneeId))
  if (filter.checklistType) constraints.push(where('checklistType', '==', filter.checklistType))
  constraints.push(...scopedReadConstraints(filter.workspaceId))
  return constraints
}


// --- Book / page / layout join ---------------------------------------------

/** Fetch today's page + layout for every book the definitions reference,
 *  deduped. Returns two maps keyed by bookId. */
const loadPagesAndLayouts = async (
  workspaceId: string,
  definitions: readonly ChecklistDefinition[],
  timeZone?: string,
): Promise<{
  pages: Map<string, ChecklistBookPage | null>
  layouts: Map<string, ChecklistLayoutVersion | null>
  detailVersions: Map<string, ChecklistDetailVersion | null>
}> => {
  const bookIds = new Set<string>()
  definitions.forEach((def) => bookIds.add(bookIdFor(workspaceId, def.checklistType)))
  const pages = new Map<string, ChecklistBookPage | null>()
  const layouts = new Map<string, ChecklistLayoutVersion | null>()
  const detailVersions = new Map<string, ChecklistDetailVersion | null>()
  const resolvedTz = timeZone ?? (definitions.length > 0 ? await fetchWorkspaceRawTimezone(workspaceId) : undefined)
  await Promise.all(
    Array.from(bookIds).map(async (bookId) => {
      const type = bookId.endsWith('_DAILY') ? 'DAILY'
        : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
        : 'INDIVIDUAL'
      const pageKey = pageKeyFor(type, new Date(), resolvedTz)
      const page = await getPage(bookId, pageKey)
      pages.set(bookId, page)
      if (page?.layoutVersionId) {
        const layout = await getLayoutVersion(bookId, page.layoutVersionId).catch(() => null)
        layouts.set(bookId, layout)
      } else {
        layouts.set(bookId, null)
      }
      if (page?.detailVersionId) {
        const detailVersion = await getDetailVersion(bookId, page.detailVersionId).catch(() => null)
        detailVersions.set(bookId, detailVersion)
      } else {
        detailVersions.set(bookId, null)
      }
    }),
  )
  return { pages, layouts, detailVersions }
}

const projectAll = (
  workspaceId: string,
  definitions: readonly ChecklistDefinition[],
  pages: Map<string, ChecklistBookPage | null>,
  layouts: Map<string, ChecklistLayoutVersion | null>,
  detailVersions: Map<string, ChecklistDetailVersion | null>,
  timeZone?: string,
): ChecklistItem[] =>
  definitions.map((def) => {
    const bookId = bookIdFor(workspaceId, def.checklistType)
    const page = pages.get(bookId)
    const layout = layouts.get(bookId)
    const detailVersion = detailVersions.get(bookId)
    // Recurring pages are created lazily; fall back to today's key so the
    // projection can still derive per-occurrence schedule dates when the
    // page doc hasn't been minted yet.
    const fallbackKey = pageKeyFor(def.checklistType, new Date(), timeZone)
    return projectDefinitionToChecklistItem(def, {
      completion: page?.completions?.[def.id],
      layoutNode: layout?.nodes?.[def.id],
      detailVersion: detailVersion ?? null,
      pageDetailPatch: page?.detailPatch?.[def.id] ?? null,
      pageKey: page?.pageKey ?? fallbackKey,
    })
  })

// --- One-shot fetch --------------------------------------------------------

export const getChecklistItemsView = async (filter: ChecklistFilter): Promise<ChecklistItem[]> => {
  const constraints = buildConstraints(filter)
  const snap = await getDocs(query(collection(firestoreDb, CHECKLISTS_COL), ...constraints))
  const definitions = snap.docs
    .map((d) => d.data() as ChecklistDefinition)
    .filter((def) => filter.includeArchived || !def.isArchived)
  const { pages, layouts, detailVersions } = await loadPagesAndLayouts(filter.workspaceId, definitions, filter.timeZone)
  return projectAll(filter.workspaceId, definitions, pages, layouts, detailVersions, filter.timeZone)
}

// --- Live subscription -----------------------------------------------------

/**
 * Subscribe to a filtered view of the workspace's checklists. Emits a
 * projected `ChecklistItem[]` on every change to any of:
 *   • the definitions matching `filter`
 *   • today's page for each book those definitions belong to
 *
 * Layouts are fetched once per (bookId, versionId) and re-fetched when a
 * page's `layoutVersionId` changes.
 */
export const subscribeChecklistItemsView = (
  filter: ChecklistFilter,
  onChange: (items: ChecklistItem[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe => {
  const constraints = buildConstraints(filter)
  let definitions: ChecklistDefinition[] = []
  const pages = new Map<string, ChecklistBookPage | null>()
  const layouts = new Map<string, ChecklistLayoutVersion | null>()
  const detailVersions = new Map<string, ChecklistDetailVersion | null>()
  const pageUnsubs = new Map<string, Unsubscribe>()
  const currentPageKeys = new Map<string, string>()
  let resolvedTz = filter.timeZone
  let isUnsubscribed = false

  const recompute = () => {
    onChange(projectAll(filter.workspaceId, definitions, pages, layouts, detailVersions, resolvedTz))
  }

  const subscribeToPageForBook = (bookId: string, type: ChecklistType) => {
    pageUnsubs.get(bookId)?.()
    const pageKey = pageKeyFor(type, new Date(), resolvedTz)
    currentPageKeys.set(bookId, pageKey)
    const unsub = onSnapshot(
      doc(firestoreDb, `checklist_books/${bookId}/pages/${pageKey}`),
      async (snap) => {
        const page = snap.exists() ? (snap.data() as ChecklistBookPage) : null
        primeChecklistPageCache(bookId, pageKey, page)
        pages.set(bookId, page)
        if (page?.layoutVersionId) {
          const layout = await getLayoutVersion(bookId, page.layoutVersionId).catch(() => null)
          layouts.set(bookId, layout)
        } else {
          layouts.set(bookId, null)
        }
        if (page?.detailVersionId) {
          const detailVersion = await getDetailVersion(bookId, page.detailVersionId).catch(() => null)
          detailVersions.set(bookId, detailVersion)
        } else {
          detailVersions.set(bookId, null)
        }
        recompute()
      },
      (err) => {
        // Page-listener failure is a partial degradation: definitions may still
        // be readable so items render with default (empty) page state. Only the
        // definitions listener error is treated as fatal (toast + empty state).
        console.warn('[checklistReader] page listener failed', err)
      },
    )
    pageUnsubs.set(bookId, unsub)
  }

  void fetchWorkspaceRawTimezone(filter.workspaceId).then((tz) => {
    if (isUnsubscribed) return
    if (tz && !filter.timeZone) {
      const prevTz = resolvedTz
      resolvedTz = tz
      if (tz !== prevTz) {
        const now = new Date()
        for (const [bookId, currentKey] of Array.from(currentPageKeys.entries())) {
          const type = bookId.endsWith('_DAILY') ? 'DAILY'
            : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
            : 'INDIVIDUAL'
          if (type === 'INDIVIDUAL') continue
          const nextKey = pageKeyFor(type, now, resolvedTz)
          if (nextKey !== currentKey) {
            pages.delete(bookId)
            subscribeToPageForBook(bookId, type)
          }
        }
        recompute()
      }
    }
  })

  const ensurePageSubscription = (bookId: string) => {
    if (pageUnsubs.has(bookId)) return
    const type = bookId.endsWith('_DAILY') ? 'DAILY'
      : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
      : 'INDIVIDUAL'
    subscribeToPageForBook(bookId, type)
  }

  // Rollover watcher (L9) - re-subscribe when pageKey changes at midnight workspace time
  const rolloverInterval = setInterval(() => {
    const now = new Date()
    for (const [bookId, currentKey] of currentPageKeys.entries()) {
      const type = bookId.endsWith('_DAILY') ? 'DAILY'
        : bookId.endsWith('_WEEKLY') ? 'WEEKLY'
        : 'INDIVIDUAL'
      if (type === 'INDIVIDUAL') continue
      const nextKey = pageKeyFor(type, now, resolvedTz)
      if (nextKey !== currentKey) {
        pages.delete(bookId)
        subscribeToPageForBook(bookId, type)
      }
    }
  }, 60_000)

  const defsUnsub = onSnapshot(
    query(collection(firestoreDb, CHECKLISTS_COL), ...constraints),
    (snap) => {
      definitions = snap.docs
        .map((d) => d.data() as ChecklistDefinition)
        .filter((def) => filter.includeArchived || !def.isArchived)
      const bookIds = new Set(definitions.map((def) => bookIdFor(filter.workspaceId, def.checklistType)))
      bookIds.forEach(ensurePageSubscription)
      recompute()
    },
    (err) => {
      console.warn('[checklistReader] definitions listener failed', err)
      definitions = []
      recompute()
      onError?.(err)
    },
  )

  return () => {
    isUnsubscribed = true
    clearInterval(rolloverInterval)
    defsUnsub()
    pageUnsubs.forEach((unsub) => unsub())
    pageUnsubs.clear()
  }
}

export const subscribeChecklistItemProjection = (
  input: {
    itemId: string
    pageKey?: string | null
    timeZone?: string
  },
  onChange: (item: ChecklistItem | null) => void,
): Unsubscribe => {
  let currentDefinition: ChecklistDefinition | null = null
  let pageUnsub: Unsubscribe | null = null

  const emitProjection = async (page: ChecklistBookPage | null, pageKeyFallback?: string) => {
    if (!currentDefinition) {
      onChange(null)
      return
    }
    const bookId = bookIdFor(currentDefinition.workspaceId, currentDefinition.checklistType)
    const layoutVersion = page?.layoutVersionId
      ? await getLayoutVersion(bookId, page.layoutVersionId).catch(() => null)
      : null
    const detailVersion = page?.detailVersionId
      ? await getDetailVersion(bookId, page.detailVersionId).catch(() => null)
      : null
    onChange(projectDefinitionToChecklistItem(currentDefinition, {
      completion: page?.completions?.[currentDefinition.id],
      layoutNode: layoutVersion?.nodes?.[currentDefinition.id],
      detailVersion,
      pageDetailPatch: page?.detailPatch?.[currentDefinition.id] ?? null,
      pageKey: page?.pageKey ?? pageKeyFallback,
    }))
  }

  const defsUnsub = onSnapshot(
    doc(firestoreDb, CHECKLISTS_COL, input.itemId),
    (snap) => {
      currentDefinition = snap.exists() ? (snap.data() as ChecklistDefinition) : null
      pageUnsub?.()
      pageUnsub = null
      if (!currentDefinition || currentDefinition.isArchived) {
        onChange(null)
        return
      }
      const bookId = bookIdFor(currentDefinition.workspaceId, currentDefinition.checklistType)
      const resolvedPageKey = input.pageKey ?? pageKeyFor(currentDefinition.checklistType, new Date(), input.timeZone)
      pageUnsub = onSnapshot(
        doc(firestoreDb, `checklist_books/${bookId}/pages/${resolvedPageKey}`),
        (pageSnap) => {
          const page = pageSnap.exists() ? (pageSnap.data() as ChecklistBookPage) : null
          primeChecklistPageCache(bookId, resolvedPageKey, page)
          // Always pass the resolved key even when the page doc doesn't exist
          // yet — recurring pages are created lazily on first write, but the
          // projection still needs the key so it can derive the per-occurrence
          // schedule for the drawer's Dated/Duration section.
          void emitProjection(page, resolvedPageKey)
        },
        () => {
          void emitProjection(null, resolvedPageKey)
        },
      )
    },
    () => onChange(null),
  )

  return () => {
    defsUnsub()
    pageUnsub?.()
  }
}

export const refetchChecklistItemHead = async (id: string): Promise<
  Pick<ChecklistItem, 'boardStatus' | 'recordVersion' | 'workflowStatus' | 'notRequiredFrom'> | null
> => {
  const snap = await getDocFromServer(doc(firestoreDb, 'checklists', id))
  if (!snap.exists()) return null
  const data = snap.data() as Record<string, unknown>
  return {
    boardStatus: (data.boardStatus as ChecklistItem['boardStatus'] | undefined) ?? null,
    recordVersion: (typeof data.recordVersion === 'number' ? data.recordVersion : 1),
    workflowStatus: (data.workflowStatus as ChecklistItem['workflowStatus'] | undefined) ?? null,
    notRequiredFrom: (typeof data.notRequiredFrom === 'string' ? data.notRequiredFrom : null),
  }
}

